<template>
  <div v-if="profile.id">
    <el-dialog
        ref="missingDevice"
        title="Missing Device"
        :fullscreen="true"
        :visible.sync="openMissingDevice"
        :before-close="handleClose">
      <Troubleshooting :data="troubleshootingData" />
      <el-alert
          data-test="alert-header"
          :closable="false"
          type="error"
          show-icon
          :title="alertTitle"
          center />

      <div
          v-if="devProfile.created_at"
          style="margin: 10px 0px; text-align: center;">
        <i class="el-icon-date"></i> Device created on {{ devProfile.created_at | moment('DD MMMM, YYYY hh:mm A') }}
      </div>
      <el-row :gutter="20">
        <el-col :span="12">
          <div class="device-info">
            <div class="profile-missing-row">
              <label>Device ID</label>
              <div><b>{{ devProfile.device_id }}</b></div>
            </div>
            <div class="profile-missing-row">
              <label>Product Type</label>
              <div><b>{{ devProfile.kind }}</b></div>
            </div>
            <div class="profile-missing-row">
              <label>Serial Number (DSN)</label>
              <div><b>{{ devProfile.serial_number }}</b></div>
            </div>
            <div class="profile-missing-row">
              <label>Product description</label>
              <div><b>{{ product ? product.description : "N/D" }}</b></div>
            </div>
            <div class="profile-missing-row">
              <label>State</label>
              <div><b>{{ deviceState | capitalize }}</b></div>
            </div>
            <div class="profile-missing-row">
              <label>Retailer Name</label>
              <div><b>{{ retailerName }}</b></div>
            </div>
            <div class="profile-missing-row">
              <label>Retailer Xline</label>
              <div><b>{{ retailerXline }}</b></div>
            </div>
            <div class="profile-missing-row">
              <label>Current Stock</label>
              <div><b>{{ devProfile.current_stock || "Fresh Lot" }}</b></div>
            </div>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="device-actions">
            <div class="button-row">
              <el-tooltip
                  content="Replacement unavailable for this device"
                  :disabled="!isReplacementDisabled">
                <el-button
                    type="primary"
                    :disabled="isReplacementDisabled"
                    data-test="replacement-button"
                    @click="openReplacementForm()">
                  Start Replacement
                </el-button>
              </el-tooltip>
            </div>
            <div class="button-row">
              <el-tooltip
                  content="Refund unavailable for this device"
                  :disabled="!isRefundDisabled">
                <el-button
                    type="primary"
                    :disabled="isRefundDisabled"
                    data-test="refund-button"
                    @click="openForm('refund')">
                  Start Refund
                </el-button>
              </el-tooltip>
            </div>
            <div class="button-row">
              <el-tooltip
                  content="Parts unavailable for this device"
                  :disabled="!isPartsDisabled">
                <el-button
                    type="primary"
                    :disabled="isPartsDisabled"
                    data-test="parts-button"
                    @click="openForm('parts')">
                  Start Parts
                </el-button>
              </el-tooltip>
            </div>
          </div>
        </el-col>
      </el-row>
      <hr>
      <el-alert
          title="Current Requests"
          type="info"
          :closable="false" />
      <el-table
          :data="devProfile.requests"
          width="100%;">
        <el-table-column label="Type">
          <template slot-scope="scope">
            {{ scope.row.type.split("::")[1] }}
          </template>
        </el-table-column>
        <el-table-column
            property="uuid"
            label="UUID" />
        <el-table-column
            property="state"
            label="State" />
        <el-table-column
            property="customer_email"
            label="Customer" />
      </el-table>
    </el-dialog>
    <replacement-modal-form
        v-if="device"
        ref="replacement"
        :device="device"
        @closed-form="reloadDeviceRequests()" />
    <parts-modal-form
        v-if="device"
        ref="parts"
        :device="device"
        @closed-form="reloadDeviceRequests()" />
    <refund-modal-form
        v-if="device"
        ref="refund"
        :device="device"
        @closed-form="reloadDeviceRequests()" />
    <!-- NEW REPLACEMENT FORM DIALOG-->
    <replacement-form
        v-if="device && newReplacementFormVisible"
        ref="replacement"
        :visible.sync="newReplacementFormVisible"
        :is-supervisor="isSupervisor"
        :device="device"
        :missing-device-eligibility="eligibility" />
  </div>
</template>

<script>
import { clone, get } from "lodash";
import ReplacementModalForm from "@/containers/CustomerRequest/ReplacementModalForm";
import RefundModalForm from "@/containers/CustomerRequest/RefundModalForm";
import PartsModalForm from "@/containers/CustomerRequest/PartsModalForm";
import ReplacementForm from "@/containers/Customer/ReplacementWidget/ReplacementForm.vue";
import Troubleshooting from "@/containers/Customer/Devices/Troubleshooting";

import { createNamespacedHelpers } from "vuex";
import axios from "axios";

const { mapGetters: sharedGetters } = createNamespacedHelpers("shared");
const { mapActions: deviceActions } = createNamespacedHelpers("db/device");

export default {
  name: "DeviceMissing",
  components: {
    ReplacementModalForm,
    RefundModalForm,
    PartsModalForm,
    ReplacementForm,
    Troubleshooting,
  },
  props: {
    profile: {
      required: false,
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  data() {
    return {
      openMissingDevice: false,
      device: null,
      devProfile: {},
      form: {
        replacement: false,
        refund: false,
        parts: false,
      },
      newReplacementFormVisible: false,
      deviceState: this.profile.state,
      alertTitle: this.setAlertTitle(),
      eligibility: {},
      troubleshooting: [],
    };
  },
  computed: {
    ...sharedGetters(["userHasPermission", "products"]),
    product() {
      return (
        this.products.find((prod) => this.devProfile.kind === prod.kind) || {}
      );
    },
    retailerXline() {
      return get(this.devProfile, "retailer.xline", "N/A");
    },
    retailerName() {
      return get(this.devProfile, "retailer.name", "No Retailer");
    },
    isReplacementDisabled() {
      return (
        this.devProfile.has_replacements ||
        !this.devProfile?.product?.options?.plc?.replacement
      );
    },
    isRefundDisabled() {
      return (
        this.devProfile.has_replacements ||
        !this.devProfile?.product?.options?.plc?.refund
      );
    },
    isPartsDisabled() {
      return !this.devProfile?.product?.options?.plc?.parts;
    },
    newReplacementFlow() {
      return !!this.userHasPermission({
        permission: "newReplacementFlow",
        on: "NewUI",
      });
    },
    isSupervisor() {
      return this.$store.getters["auth/isSupervisor"];
    },
    troubleshootingData() {
      return this.troubleshooting || [];
    },
  },
  mounted: function () {
    if (document.getElementById("gPlacesScript_1") == null) {
      let googleApiPlaces = `https://maps.googleapis.com/maps/api/js?key=${process.env.GOOGLE_API_KEY}&libraries=places&language=en-US`;
      let tag = document.createElement("script");
      tag.setAttribute("src", googleApiPlaces);
      tag.setAttribute("id", "gPlacesScript_1");
      document.head.appendChild(tag);
    }
  },
  created() {
    this.getMissingDeviceEligibility().then((response) => {
      this.eligibility = response;
    });
    if (this.profile.deleted_at) {
      this.deviceState = "Deleted";
    } else if (this.profile.deactivated_at) {
      this.deviceState = "Deactivated";
    }
  },
  methods: {
    ...deviceActions(["getDeviceEligibility"]),
    async getMissingDeviceEligibility() {
      try {
        return await this.getDeviceEligibility({
          deviceId: this.profile.device_id,
        });
      } catch (error) {
        console.error("Error getting device eligibility:", error);
        return undefined;
      }
    },
    setAlertTitle() {
      if (this.profile.deleted_at) {
        return `This device was deleted on ${this.profile.deleted_at}`;
      } else if (this.profile.deactivated_at) {
        return `This device was deactivated on ${this.profile.deactivated_at}`;
      } else {
        return "This device has not been set up";
      }
    },
    openModal() {
      this.devProfile = clone(this.profile);
      this.openMissingDevice = true;
      this.loadDeviceWithTroubleshooting();
    },
    handleClose() {
      this.openMissingDevice = false;
      this.device = null;
      this.devProfile = {};
      this.$emit("close-missing");
    },
    openForm(type) {
      this.device = {
        mac_address: this.devProfile.device_id,
        type: this.devProfile.kind,
        profile: this.devProfile,
      };
      this.$nextTick(() => {
        this.$refs[type].openForm(this.device);
      });
    },
    reloadDeviceRequests() {
      axios
        .get(`v3/devices/${this.device.profile.device_id}`, {
          params: {
            includes: ["troubleshooting"],
          },
        })
        .then((response) => {
          this.devProfile = response.data.profile;
          this.troubleshooting = get(
            response.data,
            ["device", "troubleshooting"],
            []
          );
          return this.devProfile;
        })
        .catch((error) => {
          console.error(error);
        });
    },
    showNewReplacementForm(oowBypassReason = null) {
      this.newReplacementFormVisible = true;
    },
    openReplacementForm() {
      if (this.newReplacementFlow) {
        this.openNewReplacement();
      } else {
        this.openForm("replacement");
      }
    },
    openNewReplacement() {
      this.device = {
        mac_address: this.devProfile.device_id,
        type: this.devProfile.kind,
        profile: this.devProfile,
      };
      this.showNewReplacementForm();
    },
    loadDeviceWithTroubleshooting() {
      const deviceId = this.profile.device_id;
      if (deviceId) {
        axios
          .get(`v3/devices/${deviceId}`, {
            params: {
              includes: ["troubleshooting"],
            },
          })
          .then((response) => {
            this.troubleshooting = get(
              response.data,
              ["device", "troubleshooting"],
              []
            );

            if (response.data.profile) {
              this.devProfile = {
                ...this.devProfile,
                ...response.data.profile,
              };
            }
          })
          .catch((error) => {
            console.error("Error loading troubleshooting data:", error);
          });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.device-info {
  border-right: solid 1px #eee;
  padding-right: 30px;
  .profile-missing-row {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 10px 0px;
    border-bottom: solid 1px #eee;
  }
}
hr {
  margin: 30px 0px;
  border-top: solid 1px #eee;
}
.device-actions {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-left: 10px;
  .button-row {
    margin: 10px 0px 10px 0px;
    .el-button {
      width: 100%;
    }
  }
}
</style>
